import React from 'react';
import type {
  WhatsAppMessage,
  TemplateButton,
  HeaderType,
} from '../../types/message';
import { SENDER_TYPE } from '../../types/message';
import { MessageHeader } from './MessageHeader';
import { MessageStatus } from './MessageStatus';
import { TemplateButtons } from './TemplateButtons';
import { InteractiveButtons } from './InteractiveButtons';
import { CarouselCards } from './CarouselCards';
import { MessageReactions } from './MessageReactions';

interface WhatsAppMessageCardProps {
  message: WhatsAppMessage;
  onButtonClick?: (button: TemplateButton) => void;
  onInteractiveButtonClick?: (buttonId: string, buttonTitle: string) => void;
}

export const WhatsAppMessageCard: React.FC<WhatsAppMessageCardProps> = ({
  message,
  onButtonClick,
  onInteractiveButtonClick,
}) => {
  const isOutbound =
    message.sender_type === SENDER_TYPE.BOT ||
    message.sender_type === SENDER_TYPE.AGENT;

  // Determine what to render
  const hasCarousel = message.carousel_cards && message.carousel_cards.length > 0;
  const hasHeader = message.header_type && (message.header_media_url || message.header_text);
  const hasTemplateButtons = message.buttons && message.buttons.length > 0 && !hasCarousel;
  const hasInteractiveButtons = message.interactive_buttons && message.interactive_buttons.length > 0;
  const hasReactions = message.reactions && message.reactions.length > 0;
  const footer = message.footer || message.footer_text;
  const bodyText = message.markdown_text || message.text;

  // Sender label for outbound messages
  const senderLabel = message.sender_type === SENDER_TYPE.BOT ? 'Bot' : undefined;

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'} max-w-[80%]`}>
        {/* Sender Label */}
        {senderLabel && (
          <div className="text-xs text-deep-sky mb-1 font-medium px-1">
            {senderLabel}
          </div>
        )}

        {/* Message Card */}
        <div
          className={`rounded-lg shadow-sketch overflow-hidden ${
            isOutbound ? 'bg-[#dcf8c6]' : 'bg-warm-white'
          }`}
        >
          {/* Header: IMAGE, VIDEO, DOCUMENT, or TEXT */}
          {hasHeader && (
            <MessageHeader
              headerType={message.header_type as HeaderType}
              headerText={message.header_text}
              headerMediaUrl={message.header_media_url}
            />
          )}

          {/* Body Content */}
          <div className="px-3 py-2">
            {/* Message Text */}
            {bodyText && (
              <p className="text-[15px] text-charcoal whitespace-pre-wrap leading-[1.35]">
                {bodyText}
              </p>
            )}

            {/* Footer */}
            {footer && (
              <p className="text-xs text-graphite mt-2">{footer}</p>
            )}

            {/* Timestamp and Status */}
            <MessageStatus
              dateTime={message.date_time}
              status={message.status}
              isOutbound={isOutbound}
              sendError={message.send_error}
            />
          </div>
        </div>

        {/* Reactions (below card) */}
        {hasReactions && <MessageReactions reactions={message.reactions!} />}

        {/* Carousel Cards (below card) */}
        {hasCarousel && (
          <CarouselCards
            cards={message.carousel_cards!}
            onButtonClick={(btn, cardIndex) => onButtonClick?.(btn)}
          />
        )}

        {/* Template Buttons (below card, not shown with carousel) */}
        {hasTemplateButtons && (
          <TemplateButtons
            buttons={message.buttons!}
            onButtonClick={onButtonClick}
          />
        )}

        {/* Interactive Quick Reply Buttons (below card) */}
        {hasInteractiveButtons && (
          <InteractiveButtons
            buttons={message.interactive_buttons!}
            onButtonClick={onInteractiveButtonClick}
          />
        )}
      </div>
    </div>
  );
};

export default WhatsAppMessageCard;
